import { useCallback, useMemo, useState } from 'react'
import type { AuditEntry } from '@lms/shared-types'
import { roleAtLeast } from '@lms/shared-types'
import { Card } from '../components/primitives/Card'
import { DateField } from '../components/primitives/DateField'
import { FadeIn } from '../components/primitives/FadeIn'
import { PageHead } from '../components/primitives/PageHead'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { OverrideRateTile } from '../components/hr/OverrideRateTile'
import { ReadableAuditRow } from '../components/hr/audit/ReadableAuditRow'
import { AuditGroupedList } from '../components/hr/audit/AuditGroupedList'
import { AUDIT_CATEGORY_OPTIONS } from '../components/hr/audit/audit-categories'
import { PaginationControls } from '../components/lists/PaginationControls'
import { listAuditPage } from '../api/audit'
import { useSession } from '../auth/auth-context'
import { flags } from '../lib/feature-flags'
import { useDebounce } from '../lib/use-debounce'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../lib/use-paged-list'

/** The audit log is grouped by object by default (one entry per object,
 *  expandable to its history); the Timeline view is the flat event feed. */
type ViewMode = 'grouped' | 'timeline'

export function HrAuditPage(): JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>('grouped')
  const grouped = viewMode === 'grouped'
  const [nameQuery, setNameQuery] = useState('')
  const [category, setCategory] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [exportError, setExportError] = useState<string | null>(null)

  const { user } = useSession()
  const isDirector =
    user != null && roleAtLeast(user.role as Parameters<typeof roleAtLeast>[0], 'director')
  const canExport = isDirector && fromDate !== '' && toDate !== ''

  const onExport = async (): Promise<void> => {
    if (!fromDate || !toDate) return
    setExportError(null)
    // The signed quarterly report only covers priority-score rules.
    const params = new URLSearchParams({
      fromDate,
      toDate,
      subjectType: 'priority_score_rules',
    })
    try {
      const resp = await fetch(`/api/v1/hr/audit/export?${params.toString()}`, {
        credentials: 'include',
      })
      if (!resp.ok) {
        setExportError(`Export failed (${resp.status})`)
        return
      }
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Filename via Content-Disposition header from server.
      a.download = ''
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  const debouncedName = useDebounce(nameQuery, 250)

  const filterArgs = useMemo(
    () => ({
      ...(debouncedName ? { q: debouncedName } : {}),
      ...(category ? { category } : {}),
    }),
    [debouncedName, category],
  )

  const pagedFetchAudit = useCallback(
    async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<AuditEntry>> => {
      const result = await listAuditPage({ ...filterArgs, page, limit: pageSize })
      return { rows: result.data, total: result.meta.total }
    },
    [filterArgs],
  )

  // The grouped view owns its own pagination inside AuditGroupedList, so this
  // hook only drives the flat timeline; disable its fetch while grouped.
  const pagedAudit = usePagedList<AuditEntry>({
    queryKey: ['audit', filterArgs],
    fetcher: pagedFetchAudit,
    enabled: !grouped,
  })

  const entries = pagedAudit.rows
  const timelineLoading = pagedAudit.isLoading

  return (
    <HrAccessGuard>
      {/* `.page` gives the same centered 1280px max-width + responsive
          horizontal gutter as the employee pages (e.g. My Leaves), so the
          audit surface doesn't stretch wider than the rest of the app. */}
      <div className="page">
        <PageHead
          eyebrow="HR · Director · Exec Director"
          title="Audit Log"
          subtitle="Read-only timeline of every state-changing action across the system."
        />

        {flags.aiOverrideReason ? (
          <div style={{ marginBottom: 24 }}>
            <OverrideRateTile />
          </div>
        ) : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="audit-category-filter"
            aria-label="Filter by category"
            style={{ ...inputStyle(), minWidth: 200 }}
          >
            <option value="">All categories</option>
            {AUDIT_CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Search by name — employee, team, or org unit"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            data-testid="audit-action-filter"
            aria-label="Search by name"
            style={{ ...inputStyle(), flex: '1 1 200px', minWidth: 160 }}
          />
          <div
            role="group"
            aria-label="Audit view mode"
            style={{ display: 'inline-flex', gap: 0, marginLeft: 'auto', alignSelf: 'center' }}
          >
            <button
              type="button"
              aria-pressed={viewMode === 'grouped'}
              onClick={() => setViewMode('grouped')}
              data-testid="audit-view-grouped"
              style={segmentStyle(viewMode === 'grouped', 'left')}
            >
              Group by object
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'timeline'}
              onClick={() => setViewMode('timeline')}
              data-testid="audit-view-timeline"
              style={segmentStyle(viewMode === 'timeline', 'right')}
            >
              Timeline
            </button>
          </div>
        </div>

        {/* Director-tier signed quarterly report of priority-score-rule changes. */}
        {isDirector ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 14,
              flexWrap: 'wrap',
            }}
          >
            <DateField
              aria-label="Export from date"
              value={fromDate || null}
              onChange={(iso) => setFromDate(iso ?? '')}
              data-testid="audit-export-from-date"
            />
            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>to</span>
            <DateField
              aria-label="Export to date"
              value={toDate || null}
              onChange={(iso) => setToDate(iso ?? '')}
              data-testid="audit-export-to-date"
            />
            <button
              type="button"
              disabled={!canExport}
              onClick={() => void onExport()}
              data-testid="audit-export-button"
              title={
                !canExport
                  ? 'Pick both dates to enable the quarterly report export'
                  : 'Download signed quarterly report CSV'
              }
              style={exportButtonStyle(canExport)}
            >
              Export quarterly report
            </button>
            {exportError != null ? (
              <span
                role="alert"
                style={{ fontSize: 12, color: 'var(--danger-fg)', marginLeft: 4 }}
              >
                {exportError}
              </span>
            ) : null}
          </div>
        ) : null}

        <Card padding={0} style={{ overflow: 'hidden' }}>
          <section data-testid="audit-list" aria-label="Audit log">
            {grouped ? (
              <AuditGroupedList
                category={category || undefined}
                q={debouncedName || undefined}
              />
            ) : timelineLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <ThinkingDots label="Loading audit" />
              </div>
            ) : entries.length === 0 ? (
              <FadeIn>
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                  No audit entries match the current filters.
                </div>
              </FadeIn>
            ) : (
              <>
                <ul
                  role="list"
                  data-testid="audit-rows"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                  }}
                >
                  {entries.map((entry) => (
                    <ReadableAuditRow key={entry.id} entry={entry} />
                  ))}
                </ul>
                <PaginationControls
                  page={pagedAudit.page}
                  pageSize={pagedAudit.pageSize}
                  total={pagedAudit.total}
                  totalPages={pagedAudit.totalPages}
                  pageSizeOptions={pagedAudit.pageSizeOptions}
                  rangeStart={pagedAudit.rangeStart}
                  rangeEnd={pagedAudit.rangeEnd}
                  canPrev={pagedAudit.canPrev}
                  canNext={pagedAudit.canNext}
                  onPageChange={pagedAudit.setPage}
                  onPageSizeChange={pagedAudit.setPageSize}
                  isFetching={pagedAudit.isFetching}
                />
              </>
            )}
          </section>
        </Card>
      </div>
    </HrAccessGuard>
  )
}

function inputStyle(): React.CSSProperties {
  return {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--surface)',
    fontSize: 13,
    fontFamily: 'inherit',
    color: 'var(--text)',
  }
}

function segmentStyle(active: boolean, side: 'left' | 'right'): React.CSSProperties {
  return {
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 500,
    padding: '0 12px',
    minHeight: 34,
    cursor: 'pointer',
    border: `1px solid ${active ? 'var(--primary-line)' : 'var(--border-strong)'}`,
    borderTopLeftRadius: side === 'left' ? 8 : 0,
    borderBottomLeftRadius: side === 'left' ? 8 : 0,
    borderTopRightRadius: side === 'right' ? 8 : 0,
    borderBottomRightRadius: side === 'right' ? 8 : 0,
    marginLeft: side === 'right' ? -1 : 0,
    background: active ? 'var(--primary-bg)' : 'var(--surface)',
    color: active ? 'var(--primary-fg)' : 'var(--text-2)',
    transition: 'background 80ms ease-out, border-color 80ms ease-out',
  }
}

function exportButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    padding: '0 18px',
    minHeight: 44,
    borderRadius: 8,
    cursor: enabled ? 'pointer' : 'not-allowed',
    border: 'none',
    background: enabled ? 'var(--steel-blue)' : 'var(--border)',
    color: enabled ? 'var(--primary-contrast, #fff)' : 'var(--text-3)',
    opacity: enabled ? 1 : 0.6,
    transition: 'background 80ms ease-out, opacity 80ms ease-out',
  }
}
